import {environment} from 'src/environments/environment';
import {inject, Injectable} from '@angular/core';
import {lastValueFrom, Observable} from 'rxjs';
import {WebArchiveResult, webArchiveResultSchema} from 'src/app/webarchive/web-archive-result';
import {HttpClient, HttpParams} from '@angular/common/http';
import {mapParseItem} from '@brunata-angular/util';
import {DateTime} from 'luxon';

export type DocumentSearchParameters = Partial<{
    buildingNo: string;
    propertyNo: string;
    branchNo: string;
    locationNo: string;
    residentNo: string;
    residentName: string;
    accountNo: string;
    period: string;
    address: string;
}>;

@Injectable({
    providedIn: 'root',
})
export class WebArchiveService {
    private http = inject(HttpClient);

    getDocuments(query: DocumentSearchParameters): Observable<WebArchiveResult> {
        const url = `${environment.webArchiveServiceUrl}/documents/search/bills`;
        const parameterMappings = [
            {key: 'buildingno', value: 'buildingNo'},
            {key: 'secondsectorno', value: 'propertyNo'},
            {key: 'firstsectorno', value: 'branchNo'},
            {key: 'locationno', value: 'locationNo'},
            {key: 'consumerno', value: 'residentNo'},
            {key: 'accountno', value: 'accountNo'},
            {key: 'periodenddate', value: 'period'},
            {key: 'consumername', value: 'residentName'},
            {key: 'consumeraddress', value: 'address'},
        ];

        const queryParams = this.mapQueryParams(query, parameterMappings);
        return this.http
            .get(url, {withCredentials: true, params: queryParams})
            .pipe(mapParseItem(webArchiveResultSchema));
    }

    async downloadDocument(docId: string, windowTarget = '_blank'): Promise<void> {
        const url = `${environment.webArchiveServiceUrl}/documents/${docId}`;
        const response = await lastValueFrom(
            this.http.get(url, {responseType: 'blob', observe: 'response'}),
        );
        if (response.body != null) {
            const downloadUrl = URL.createObjectURL(response.body);
            window.open(downloadUrl, windowTarget);
        }
    }

    async downloadDocuments(docId: string[]): Promise<void> {
        const url = `${environment.webArchiveServiceUrl}/documents/zip`;
        const response = await lastValueFrom(
            this.http.post(url, docId, {responseType: 'blob', observe: 'response'}),
        );
        if (response.body != null) {
            const date = DateTime.now().toISODate();
            const fileName = `BrunataWebArchive-Bills-${date}.zip`;
            const file = new File([response.body], fileName, {
                type: 'application/x-zip',
            });
            const exportUrl = URL.createObjectURL(file);

            // We need this hack with an anchor element to get the correct file name of the downloaded zip file.
            const anchorElement = document.createElement('a');
            document.body.appendChild(anchorElement);
            anchorElement.style.display = 'none';
            anchorElement.href = exportUrl;
            anchorElement.download = fileName;
            anchorElement.click();

            URL.revokeObjectURL(exportUrl);
        }
    }

    /**
     * Dynamically maps query parameters from an object to a representation, that is suitable for the API.
     */
    private mapQueryParams(
        query: {[k: string]: string},
        parameterMappings: {key: string; value: string}[],
    ): HttpParams {
        let queryParams = new HttpParams();
        parameterMappings.forEach(mapping => {
            if (Object.hasOwn(query, mapping.value) && query[mapping.value] !== '') {
                queryParams = queryParams.set(mapping.key, query[mapping.value]);
            }
        });
        return queryParams;
    }
}
